/**
 * MatterEstateAdminStore
 *
 * Zustand store for managing Estate Administration matter details state.
 * Replaces complex prop drilling and event system from /matters/[id] page.
 *
 * Features:
 * - Single source of truth for selected matter and active tab
 * - Integrates with React Query for data fetching
 * - Supports tab lazy loading
 * - Handles prefetch state tracking
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useMemo } from 'react';
import type { MatterTabId } from '@/lib/types/matter-navigation';

/**
 * Valid tab identifiers for estate admin matter details
 * Re-exported from matter-navigation for backwards compatibility
 */
export type MatterTab = MatterTabId;

/**
 * Store state interface
 */
interface MatterEstateAdminState {
  // ===== UI State =====
  /** Currently selected matter ID (null when panel closed) */
  selectedMatterId: number | null;

  /** Active tab in the detail panel */
  activeTab: MatterTab;

  /** Whether the detail panel is open */
  isPanelOpen: boolean;

  /** Whether the detail panel is expanded to full width (hiding middle panel) */
  isPanelExpanded: boolean;

  // ===== Prefetch Tracking =====
  /** Matter ID currently being prefetched (for cancellation) */
  activePrefetch: number | null;

  // ===== URL Sync Control =====
  /** Skip URL sync when external navigation handles URL (e.g., sidebar view change) */
  skipUrlSync: boolean;
}

/**
 * Store actions interface
 */
interface MatterEstateAdminActions {
  // ===== Panel Control =====
  /** Navigate to matter details page (replaces panel) */
  openMatter: (matterId: number, tab?: MatterTab) => void;

  /** Close detail panel and clear state */
  closeMatter: () => void;

  /** Close panel and skip URL sync (when external navigation handles URL) */
  closeMatterWithoutUrlSync: () => void;

  /** Toggle panel expanded state (full width vs normal) */
  togglePanelExpanded: () => void;

  /** Set panel expanded state directly */
  setPanelExpanded: (expanded: boolean) => void;

  // ===== Tab Navigation =====
  /** Change active tab */
  changeTab: (tab: MatterTab) => void;

  // ===== Prefetch Control =====
  /** Set which matter is being prefetched */
  setActivePrefetch: (matterId: number | null) => void;

  // ===== URL Sync Control =====
  /** Clear the skipUrlSync flag after navigation completes */
  clearSkipUrlSync: () => void;

  // ===== Utility =====
  /** Reset to initial state */
  reset: () => void;
}

/**
 * Combined store type
 */
type MatterEstateAdminStore = MatterEstateAdminState & MatterEstateAdminActions;

/**
 * Initial state
 */
const initialState: MatterEstateAdminState = {
  selectedMatterId: null,
  activeTab: 'overview',
  isPanelOpen: false,
  isPanelExpanded: false,
  activePrefetch: null,
  skipUrlSync: false
};

/**
 * MatterEstateAdminStore
 *
 * @example
 * ```tsx
 * // Navigate to matter details page
 * const { openMatter } = useMatterPanelActions();
 * openMatter(123, 'overview'); // Navigates to /matters/123/overview
 *
 * // Read state (legacy panel state - will be removed)
 * const matterId = useMatterEstateAdminStore(s => s.selectedMatterId);
 * const isOpen = useMatterEstateAdminStore(s => s.isPanelOpen);
 * ```
 */
export const useMatterEstateAdminStore = create<MatterEstateAdminStore>()(
  devtools(
    (set, _get) => ({
      // Initial state
      ...initialState,

      // Actions
      openMatter: (matterId, tab = 'overview') => {
        // Set panel state to open the matter in the slide-over panel
        // Navigation to /matters/ page is handled by click handlers for EA matters
        set(
          {
            selectedMatterId: matterId,
            isPanelOpen: true,
            activeTab: tab
          },
          false,
          'openMatter'
        );
      },

      closeMatter: () => {
        set(
          {
            selectedMatterId: null,
            isPanelOpen: false,
            isPanelExpanded: false,
            activeTab: 'overview'
          },
          false,
          'closeMatter'
        );
      },

      closeMatterWithoutUrlSync: () => {
        set(
          {
            selectedMatterId: null,
            isPanelOpen: false,
            isPanelExpanded: false,
            activeTab: 'overview',
            skipUrlSync: true
          },
          false,
          'closeMatterWithoutUrlSync'
        );
      },

      togglePanelExpanded: () => {
        set(
          (state) => ({ isPanelExpanded: !state.isPanelExpanded }),
          false,
          'togglePanelExpanded'
        );
      },

      setPanelExpanded: (expanded) => {
        set({ isPanelExpanded: expanded }, false, 'setPanelExpanded');
      },

      changeTab: (tab) => {
        set({ activeTab: tab }, false, 'changeTab');
      },

      setActivePrefetch: (matterId) => {
        set({ activePrefetch: matterId }, false, 'setActivePrefetch');
      },

      clearSkipUrlSync: () => {
        set({ skipUrlSync: false }, false, 'clearSkipUrlSync');
      },

      reset: () => {
        set(initialState, false, 'reset');
      }
    }),
    {
      name: 'MatterEstateAdminStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

/**
 * Selector hooks for common use cases
 * Prevents unnecessary re-renders by selecting only needed state
 */

/** Get currently selected matter ID */
export const useSelectedMatterId = () =>
  useMatterEstateAdminStore((s) => s.selectedMatterId);

/**
 * Get whether panel is open.
 *
 * NOTE: sole consumer is components/dashboard/create-matter-dropdown.tsx,
 * which is itself dead (Tier-A1, FE-9/FE-10 scope). Excluded from FE-12's
 * removal for the same reason FE-6 excluded matter-actions.ts et al: a dead
 * importer that still exists in the repo still needs its import to resolve.
 */
export const useIsPanelOpen = () =>
  useMatterEstateAdminStore((s) => s.isPanelOpen);

/** Get all panel actions - using store directly for stable reference */
export const getMatterPanelActions = () => ({
  openMatter: useMatterEstateAdminStore.getState().openMatter,
  closeMatter: useMatterEstateAdminStore.getState().closeMatter,
  closeMatterWithoutUrlSync:
    useMatterEstateAdminStore.getState().closeMatterWithoutUrlSync,
  changeTab: useMatterEstateAdminStore.getState().changeTab,
  togglePanelExpanded: useMatterEstateAdminStore.getState().togglePanelExpanded,
  setPanelExpanded: useMatterEstateAdminStore.getState().setPanelExpanded,
  clearSkipUrlSync: useMatterEstateAdminStore.getState().clearSkipUrlSync
});

/**
 * Hook version - memoized to return stable reference.
 *
 * NOTE: sole consumer is components/institutions/inventory-list.tsx, which
 * is itself dead (Tier-A1, FE-9/FE-10 scope). Excluded from FE-12's removal
 * for the same reason FE-6 excluded matter-actions.ts et al: a dead importer
 * that still exists in the repo still needs its import to resolve.
 */
export const useMatterPanelActions = () => {
  return useMemo(() => getMatterPanelActions(), []);
};
